import React, { useState, useMemo } from 'react'
import { useQuery } from 'react-query'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Container,
  Heading,
  Text,
  Badge,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Tag,
  IconButton,
  Flex,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useBreakpointValue,
  useColorModeValue,
  Icon,
  Circle,
  Progress,
  Tooltip,
  keyframes,
} from '@chakra-ui/react'
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon, LockIcon, CheckCircleIcon, TimeIcon } from '@chakra-ui/icons'
import { FiCpu, FiZap, FiTrendingUp, FiTarget, FiAward } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'

// Animations
const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

interface Problem {
  id?: string
  const_id: string
  title: string
  short_description: string
  tags: string
  max_tiers: number
  user_progress?: number | null
}

export default function ProblemList() {
  const { isAuthenticated, getAccessToken, isLoading: isAuthLoading } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [difficultyFilter, setDifficultyFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const problemsPerPage = 12

  // Theme colors
  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.800')
  const cardBorder = useColorModeValue('gray.200', 'gray.700')
  const textColor = useColorModeValue('gray.800', 'white')
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400')
  const statBg = useColorModeValue('white', 'gray.800')

  // Helper functions - moved before usage to avoid initialization order issues
  const getDifficultyFromTiers = (maxTiers: number) => {
    if (maxTiers <= 3) return 'Easy'
    if (maxTiers <= 5) return 'Medium'
    return 'Hard'
  }

  const getStatusFromProgress = (userProgress?: number | null, maxTiers?: number) => {
    // If user is not authenticated, always show as not attempted
    if (!isAuthenticated || !userProgress || userProgress === 0) return 'not_attempted'
    if (userProgress >= (maxTiers || 1)) return 'solved'
    return 'attempted'
  }

  const getDifficultyColor = (maxTiers: number) => {
    const difficulty = getDifficultyFromTiers(maxTiers)
    switch (difficulty) {
      case 'Easy': return { bg: 'green.400', text: 'green.900' }
      case 'Medium': return { bg: 'yellow.400', text: 'yellow.900' }
      case 'Hard': return { bg: 'red.400', text: 'red.900' }
      default: return { bg: 'gray.400', text: 'gray.900' }
    }
  }

  const getStatusIcon = (userProgress?: number | null, maxTiers?: number) => {
    const status = getStatusFromProgress(userProgress, maxTiers)
    switch (status) {
      case 'solved': return <CheckCircleIcon color="green.400" boxSize={5} />
      case 'attempted': return <TimeIcon color="yellow.400" boxSize={5} />
      default: return <LockIcon color="gray.500" boxSize={4} />
    }
  }

  const getProgressPercentage = (userProgress?: number | null, maxTiers?: number) => {
    if (!isAuthenticated || !userProgress || !maxTiers || maxTiers === 0) return 0
    return Math.round((userProgress / maxTiers) * 100)
  }

  const getProgressColor = (userProgress?: number | null, maxTiers?: number) => {
    const percentage = getProgressPercentage(userProgress, maxTiers)
    if (percentage >= 100) return 'green'
    if (percentage >= 50) return 'yellow'
    if (percentage > 0) return 'orange'
    return 'gray'
  }

  const { data: problemData, isLoading, error } = useQuery<{problems: Problem[], userinfo: any}>(
    'problems',
    async () => {
      // No need for Bearer token - using session-based auth
      const response = await api.get('/problem_list')
      return response.data
    },
    {
      enabled: !isAuthLoading, // Allow loading for both authenticated and non-authenticated users
    }
  )

  // Extract problems from the response
  const problems = problemData?.problems || []

  // Filter problems based on search and filters
  const filteredProblems = useMemo(() => {
    if (!problems) return []
    
    return problems.filter(problem => {
      // Search filter
      if (searchQuery && !problem.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !problem.short_description.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !problem.tags.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false
      }
      
      // Difficulty filter based on max_tiers
      if (difficultyFilter !== 'all') {
        const difficulty = getDifficultyFromTiers(problem.max_tiers)
        if (difficulty !== difficultyFilter) {
          return false
        }
      }
      
      // Status filter based on user_progress
      if (statusFilter !== 'all') {
        const status = getStatusFromProgress(problem.user_progress, problem.max_tiers)
        if (status !== statusFilter) {
          return false
        }
      }
      
      // Category filter
      if (categoryFilter !== 'all' && !problem.tags.toLowerCase().includes(categoryFilter.toLowerCase())) {
        return false
      }
      
      return true
    })
  }, [problems, searchQuery, difficultyFilter, statusFilter, categoryFilter])

  // Pagination
  const totalPages = Math.ceil(filteredProblems.length / problemsPerPage)
  const paginatedProblems = filteredProblems.slice(
    (currentPage - 1) * problemsPerPage,
    currentPage * problemsPerPage
  )

  // Calculate stats
  const stats = useMemo(() => {
    if (!problems) return { total: 0, solved: 0, attempted: 0, todo: 0 }
    
    // If user is not authenticated, show total problems but no progress stats
    if (!isAuthenticated) {
      return {
        total: problems.length,
        solved: 0,
        attempted: 0,
        todo: problems.length,
      }
    }
    
    return {
      total: problems.length,
      solved: problems.filter(p => p.user_progress && p.user_progress >= p.max_tiers).length,
      attempted: problems.filter(p => p.user_progress && p.user_progress > 0 && p.user_progress < p.max_tiers).length,
      todo: problems.filter(p => !p.user_progress || p.user_progress === 0).length,
    }
  }, [problems, isAuthenticated])

  // Extract unique categories from tags
  const categories = useMemo(() => {
    const allTags = new Set<string>()
    problems.forEach(problem => {
      problem.tags.split(',').forEach(tag => {
        const trimmed = tag.trim()
        if (trimmed) allTags.add(trimmed)
      })
    })
    return Array.from(allTags).sort()
  }, [problems])

  // Allow non-authenticated users to view problems
  // Authentication check removed to enable public problem viewing

  if (isLoading) {
    return (
      <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.400" thickness="4px" />
          <Text color={mutedTextColor}>Loading problems...</Text>
        </VStack>
      </Box>
    )
  }

  if (error) {
    return (
      <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center">
        <Alert status="error" variant="subtle" borderRadius="xl" maxW="md">
          <AlertIcon />
          <Text>Failed to load problems. Please try again.</Text>
        </Alert>
      </Box>
    )
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Hero Section */}
      <Box 
        bg={useColorModeValue(
          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          "linear-gradient(135deg, #2d3561 0%, #3b2c5a 50%, #4a3b6b 100%)"
        )}
        py={12}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.2}
          bg={useColorModeValue("white", "blackAlpha.300")}
          backgroundImage={useColorModeValue(
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 1px)",
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.1) 1px, transparent 1px)"
          )}
          backgroundSize="40px 40px"
        />
        <Container maxW="container.xl" position="relative">
          <VStack spacing={8} align="stretch">
            <VStack spacing={4} textAlign="center">
              <Heading 
                size="2xl" 
                color="white" 
                fontWeight="800"
                textShadow="0 2px 4px rgba(0,0,0,0.1)"
              >
                Master Performance Engineering
              </Heading>
              <Text fontSize="xl" color="whiteAlpha.900" maxW="2xl" mx="auto">
                Challenge yourself with optimization problems designed to push your code to its limits
              </Text>
            </VStack>

            {/* Search and Filters */}
            <Box bg="whiteAlpha.200" backdropFilter="blur(10px)" p={6} borderRadius="2xl">
              <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
                <InputGroup flex={1} size="lg">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="whiteAlpha.700" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search problems by title, tags, or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    bg="whiteAlpha.300"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'whiteAlpha.700' }}
                    _hover={{ bg: 'whiteAlpha.400' }}
                    _focus={{ bg: 'whiteAlpha.500', boxShadow: 'none' }}
                  />
                </InputGroup>
                
                <HStack spacing={3} flexWrap="wrap">
                  <Select
                    value={difficultyFilter}
                    onChange={(e) => setDifficultyFilter(e.target.value)}
                    w={{ base: 'full', md: '150px' }}
                    bg="whiteAlpha.300"
                    border="none"
                    color="white"
                    size="lg"
                    _hover={{ bg: 'whiteAlpha.400' }}
                    sx={{
                      option: {
                        bg: 'gray.800',
                        color: 'white',
                        _hover: { bg: 'gray.700' }
                      }
                    }}
                  >
                    <option value="all">All Levels</option>
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </Select>
                  
                  <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    w={{ base: 'full', md: '150px' }}
                    bg="whiteAlpha.300"
                    border="none"
                    color="white"
                    size="lg"
                    _hover={{ bg: 'whiteAlpha.400' }}
                    sx={{
                      option: {
                        bg: 'gray.800',
                        color: 'white',
                        _hover: { bg: 'gray.700' }
                      }
                    }}
                  >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat.toLowerCase()}>{cat}</option>
                    ))}
                  </Select>
                  
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    w={{ base: 'full', md: '150px' }}
                    bg="whiteAlpha.300"
                    border="none"
                    color="white"
                    size="lg"
                    _hover={{ bg: 'whiteAlpha.400' }}
                    sx={{
                      option: {
                        bg: 'gray.800',
                        color: 'white',
                        _hover: { bg: 'gray.700' }
                      }
                    }}
                  >
                    <option value="all">All Status</option>
                    <option value="solved">Solved</option>
                    <option value="attempted">In Progress</option>
                    <option value="not_attempted">Not Started</option>
                  </Select>
                </HStack>
              </Flex>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxW="container.xl" mt={-8} position="relative" zIndex={1}>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
          <Box
            bg={statBg}
            p={6}
            borderRadius="xl"
            boxShadow="xl"
            border="1px solid"
            borderColor={cardBorder}
            position="relative"
            overflow="hidden"
            transition="all 0.3s"
            _hover={{ transform: 'translateY(-4px)', boxShadow: '2xl' }}
          >
            <Box
              position="absolute"
              top={0}
              right={0}
              w="100px"
              h="100px"
              bg="blue.500"
              opacity={0.1}
              borderRadius="full"
              transform="translate(30px, -30px)"
            />
            <Stat position="relative">
              <StatLabel color={mutedTextColor} fontSize="sm" fontWeight="600">Total Problems</StatLabel>
              <StatNumber fontSize="3xl" fontWeight="800" color="blue.400">
                {stats.total}
              </StatNumber>
              <StatHelpText color={mutedTextColor}>
                <Icon as={FiTarget} mr={1} />
                Ready to solve
              </StatHelpText>
            </Stat>
          </Box>
          
          <Box
            bg={statBg}
            p={6}
            borderRadius="xl"
            boxShadow="xl"
            border="1px solid"
            borderColor={cardBorder}
            position="relative"
            overflow="hidden"
            transition="all 0.3s"
            _hover={{ transform: 'translateY(-4px)', boxShadow: '2xl' }}
          >
            <Box
              position="absolute"
              top={0}
              right={0}
              w="100px"
              h="100px"
              bg="green.500"
              opacity={0.1}
              borderRadius="full"
              transform="translate(30px, -30px)"
            />
            <Stat position="relative">
              <StatLabel color={mutedTextColor} fontSize="sm" fontWeight="600">Solved</StatLabel>
              <StatNumber fontSize="3xl" fontWeight="800" color="green.400">
                {stats.solved}
              </StatNumber>
              <StatHelpText color={mutedTextColor}>
                <Progress 
                  value={(stats.solved / stats.total) * 100} 
                  size="xs" 
                  colorScheme="green" 
                  borderRadius="full"
                  bg={useColorModeValue('gray.200', 'gray.700')}
                />
              </StatHelpText>
            </Stat>
          </Box>
          
          <Box
            bg={statBg}
            p={6}
            borderRadius="xl"
            boxShadow="xl"
            border="1px solid"
            borderColor={cardBorder}
            position="relative"
            overflow="hidden"
            transition="all 0.3s"
            _hover={{ transform: 'translateY(-4px)', boxShadow: '2xl' }}
          >
            <Box
              position="absolute"
              top={0}
              right={0}
              w="100px"
              h="100px"
              bg="yellow.500"
              opacity={0.1}
              borderRadius="full"
              transform="translate(30px, -30px)"
            />
            <Stat position="relative">
              <StatLabel color={mutedTextColor} fontSize="sm" fontWeight="600">In Progress</StatLabel>
              <StatNumber fontSize="3xl" fontWeight="800" color="yellow.400">
                {stats.attempted}
              </StatNumber>
              <StatHelpText color={mutedTextColor}>
                <Icon as={FiTrendingUp} mr={1} />
                Keep going!
              </StatHelpText>
            </Stat>
          </Box>
          
          <Box
            bg={statBg}
            p={6}
            borderRadius="xl"
            boxShadow="xl"
            border="1px solid"
            borderColor={cardBorder}
            position="relative"
            overflow="hidden"
            transition="all 0.3s"
            _hover={{ transform: 'translateY(-4px)', boxShadow: '2xl' }}
          >
            <Box
              position="absolute"
              top={0}
              right={0}
              w="100px"
              h="100px"
              bg="purple.500"
              opacity={0.1}
              borderRadius="full"
              transform="translate(30px, -30px)"
            />
            <Stat position="relative">
              <StatLabel color={mutedTextColor} fontSize="sm" fontWeight="600">Completion</StatLabel>
              <StatNumber fontSize="3xl" fontWeight="800" color="purple.400">
                {stats.total > 0 ? Math.round((stats.solved / stats.total) * 100) : 0}%
              </StatNumber>
              <StatHelpText color={mutedTextColor}>
                <Icon as={FiAward} mr={1} />
                Overall progress
              </StatHelpText>
            </Stat>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Problems Grid */}
      <Container maxW="container.xl" py={8}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {paginatedProblems.map((problem, index) => {
            const difficulty = getDifficultyFromTiers(problem.max_tiers)
            const progressPercentage = getProgressPercentage(problem.user_progress, problem.max_tiers)
            
            return (
              <Box
                key={problem.const_id}
                as={RouterLink}
                to={`/problem/${problem.const_id}`}
                display="block"
                bg={cardBg}
                borderRadius="xl"
                overflow="hidden"
                border="1px solid"
                borderColor={cardBorder}
                transition="all 0.3s"
                _hover={{ 
                  transform: 'translateY(-4px)', 
                  boxShadow: useColorModeValue(
                    '0 12px 24px rgba(0,0,0,0.1)',
                    '0 12px 24px rgba(0,0,0,0.3)'
                  ),
                  borderColor: 'brand.400',
                }}
                animation={`${fadeIn} 0.5s ease-out ${index * 0.1}s both`}
                position="relative"
              >
                {/* Difficulty Badge */}
                <Box
                  position="absolute"
                  top={4}
                  right={4}
                  zIndex={1}
                >
                  <Badge
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="700"
                    bg={getDifficultyColor(problem.max_tiers).bg}
                    color={getDifficultyColor(problem.max_tiers).text}
                    textTransform="uppercase"
                    boxShadow="md"
                  >
                    {difficulty}
                  </Badge>
                </Box>

                {/* Card Header */}
                <Box p={6} pb={0}>
                  <HStack spacing={3} mb={3}>
                    <Circle
                      size="40px"
                      bg={getStatusFromProgress(problem.user_progress, problem.max_tiers) === 'solved' ? 'green.400' : 
                          getStatusFromProgress(problem.user_progress, problem.max_tiers) === 'attempted' ? 'yellow.400' : 'gray.600'}
                      color={getStatusFromProgress(problem.user_progress, problem.max_tiers) === 'solved' ? 'green.900' : 
                             getStatusFromProgress(problem.user_progress, problem.max_tiers) === 'attempted' ? 'yellow.900' : 'gray.400'}
                    >
                      {getStatusIcon(problem.user_progress, problem.max_tiers)}
                    </Circle>
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontSize="xs" color={mutedTextColor} fontWeight="600" textTransform="uppercase">
                        Max Tiers: {problem.max_tiers}
                      </Text>
                      <Heading size="md" color={textColor} noOfLines={1}>
                        {problem.title}
                      </Heading>
                    </VStack>
                  </HStack>

                  <Text color={mutedTextColor} fontSize="sm" noOfLines={2} mb={4}>
                    {problem.short_description}
                  </Text>

                  {/* Tags */}
                  <HStack spacing={2} mb={4} flexWrap="wrap">
                    {problem.tags.split(',').slice(0, 3).map((tag, idx) => (
                      <Tag
                        key={idx}
                        size="sm"
                        borderRadius="full"
                        bg={useColorModeValue('brand.50', 'brand.900')}
                        color={useColorModeValue('brand.700', 'brand.200')}
                        fontSize="xs"
                        fontWeight="600"
                      >
                        {tag.trim()}
                      </Tag>
                    ))}
                  </HStack>
                </Box>

                {/* Stats Section */}
                <Box 
                  bg={useColorModeValue('gray.100', 'gray.900')} 
                  p={4} 
                  borderTop="1px solid" 
                  borderColor={cardBorder}
                >
                  {/* Progress Text */}
                  <HStack justify="space-between" mb={3}>
                    <Text color={mutedTextColor} fontSize="sm" fontWeight="600">
                      {isAuthenticated ? 'Progress' : 'Tiers Available'}
                    </Text>
                    <HStack spacing={2}>
                      <Text color={getProgressColor(problem.user_progress, problem.max_tiers) + '.400'} fontSize="sm" fontWeight="700">
                        {isAuthenticated ? `${problem.user_progress || 0} / ${problem.max_tiers}` : `${problem.max_tiers} tiers`}
                      </Text>
                      {isAuthenticated && (
                        <Text color={mutedTextColor} fontSize="sm">
                          ({progressPercentage}%)
                        </Text>
                      )}
                    </HStack>
                  </HStack>

                  {/* Progress Bar */}
                  <Progress
                    value={isAuthenticated ? progressPercentage : 0}
                    size="sm"
                    colorScheme={isAuthenticated ? getProgressColor(problem.user_progress, problem.max_tiers) : 'gray'}
                    borderRadius="full"
                    bg={useColorModeValue('gray.200', 'gray.700')}
                  />
                </Box>

                {/* Hover Overlay */}
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  bg="linear-gradient(135deg, transparent 0%, rgba(99, 102, 241, 0.1) 100%)"
                  opacity={0}
                  transition="opacity 0.3s"
                  _groupHover={{ opacity: 1 }}
                  pointerEvents="none"
                />
              </Box>
            )
          })}
        </SimpleGrid>

        {/* Pagination */}
        {totalPages > 1 && (
          <HStack justify="center" spacing={2} mt={10}>
            <IconButton
              aria-label="Previous page"
              icon={<ChevronLeftIcon />}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              isDisabled={currentPage === 1}
              colorScheme="brand"
              variant="ghost"
              size="lg"
            />
            
            {[...Array(Math.min(5, totalPages))].map((_, idx) => {
              const pageNum = idx + 1
              return (
                <Button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  colorScheme={currentPage === pageNum ? 'brand' : 'gray'}
                  variant={currentPage === pageNum ? 'solid' : 'ghost'}
                  size="lg"
                  fontWeight="700"
                >
                  {pageNum}
                </Button>
              )
            })}
            
            {totalPages > 5 && (
              <>
                <Text color={mutedTextColor}>...</Text>
                <Button
                  onClick={() => setCurrentPage(totalPages)}
                  colorScheme={currentPage === totalPages ? 'brand' : 'gray'}
                  variant={currentPage === totalPages ? 'solid' : 'ghost'}
                  size="lg"
                  fontWeight="700"
                >
                  {totalPages}
                </Button>
              </>
            )}
            
            <IconButton
              aria-label="Next page"
              icon={<ChevronRightIcon />}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              isDisabled={currentPage === totalPages}
              colorScheme="brand"
              variant="ghost"
              size="lg"
            />
          </HStack>
        )}
      </Container>
    </Box>
  )
} 